import { useState } from 'react';
import type { QuoteResult } from '@ox/schema';
import { useEditorStore } from '../store/editor-store';
import { PromoModal } from './PromoModal';
import { PriceBreakdownSheet } from './PriceBreakdownSheet';

const DEFAULT_PROMO_LINES = [
  'Descuentos por cantidad al personalizar tu producto.',
  'Cuantos más artículos añadas, mayor puede ser el descuento.',
  'El descuento se aplica automáticamente al calcular el total.',
];

export function FooterBar({
  onSave,
  onChooseSize,
  onOpenProduct,
  saving,
  saveLabel = 'Guardar',
  chooseSizeLabel = '🛒 Elegir talla y cantidad',
  notice,
  priceLabel,
  quote,
  showCommerce = true,
}: {
  onSave: () => void;
  onChooseSize: () => void;
  /** Opens change-product with current model selected. */
  onOpenProduct?: () => void;
  saving: boolean;
  saveLabel?: string;
  chooseSizeLabel?: string;
  notice?: string | null;
  priceLabel?: string;
  quote?: QuoteResult | null;
  /** When false, hide size/qty buy button (design-only embeds). */
  showCommerce?: boolean;
}) {
  const model = useEditorStore((s) => s.model);
  const colorKey = useEditorStore((s) => s.document.selection.colorKey);
  const [promoOpen, setPromoOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  if (!model) return null;

  const promo = model.promoNote;
  const volumeTiers = promo?.volumeTiers;
  const isSimpleOffer =
    !!volumeTiers && volumeTiers.length > 0 && volumeTiers.every((t) => t.kind === 'simple');
  const promoTitle = isSimpleOffer
    ? 'Descuento'
    : (promo?.modalTitle ?? 'Descuentos por volumen');
  const showCustomText = promo?.showCustomText !== false && !isSimpleOffer;
  const membership = promo?.membership;
  const printTiers = promo?.printTiers;
  const hasLiveOffers =
    (!!membership?.active) ||
    (!!printTiers && printTiers.length > 0) ||
    (!!volumeTiers && volumeTiers.length > 0);
  const promoHtml = showCustomText ? promo?.modalHtml?.trim() : undefined;
  const promoLines =
    showCustomText && promo?.modalLines && promo.modalLines.length > 0
      ? promo.modalLines
      : showCustomText && !promoHtml && !hasLiveOffers
        ? DEFAULT_PROMO_LINES
        : undefined;
  const showPromo = hasLiveOffers || (showCustomText && !!promo?.text);
  const deliveryHtml = model.deliveryHtml?.trim();
  const displayPrice =
    priceLabel ??
    `${model.priceFrom.currency} ${model.priceFrom.amount.toFixed(2)}`;
  const colorLabel = model.colors.find((c) => c.key === colorKey)?.label ?? colorKey;
  const canOpenBreakdown = !!quote?.total;

  return (
    <>
      <footer className="ox-footer">
        <div className="ox-footer-left">
          <button
            type="button"
            className="ox-footer-product"
            onClick={() => onOpenProduct?.()}
            disabled={!onOpenProduct}
            aria-label="Ver o cambiar producto"
          >
            <span className="ox-footer-product-name">
              <strong>{model.name}</strong>
              <span className="ox-footer-info" aria-hidden>
                i
              </span>
            </span>
            {model.rating && (
              <span className="ox-footer-rating">
                ★ {model.rating.value} ({model.rating.count})
              </span>
            )}
          </button>
          <div className="ox-price-block">
            <button
              type="button"
              className="ox-live-price"
              disabled={!canOpenBreakdown}
              onClick={() => canOpenBreakdown && setBreakdownOpen(true)}
              aria-label="Desglose de precios"
            >
              <span className="ox-price">{displayPrice}</span>
              {canOpenBreakdown && (
                <span className="ox-price-chevron ox-price-chevron--down" aria-hidden>
                  ⌄
                </span>
              )}
            </button>
            {showPromo && (
              <button
                type="button"
                className="ox-promo-link"
                onClick={() => setPromoOpen(true)}
              >
                {promo?.text || 'Ofertas'} <span className="ox-promo-more">más info</span>
              </button>
            )}
          </div>
          {deliveryHtml ? (
            <div
              className="ox-ship ox-rich-html"
              dangerouslySetInnerHTML={{ __html: deliveryHtml }}
            />
          ) : (
            model.deliveryEstimates &&
            model.deliveryEstimates.length > 0 && (
              <div className="ox-ship">
                {model.deliveryEstimates.map((d) => (
                  <div key={d.label}>
                    {d.label}: {d.value}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
        <div className="ox-footer-actions">
          <button type="button" onClick={onSave} disabled={saving}>
            {saving ? 'Guardando…' : saveLabel}
          </button>
          {showCommerce && (
            <button type="button" className="ox-primary" onClick={onChooseSize}>
              {chooseSizeLabel}
            </button>
          )}
        </div>
        {notice && <div className="ox-footer-notice">{notice}</div>}
      </footer>
      {promoOpen && showPromo && (
        <PromoModal
          title={promoTitle}
          html={promoHtml}
          lines={promoHtml ? undefined : promoLines}
          footnote={showCustomText ? promo?.modalFootnote : undefined}
          showCustomText={showCustomText}
          volumeTiers={volumeTiers}
          membership={membership}
          printTiers={printTiers}
          onClose={() => setPromoOpen(false)}
        />
      )}
      {breakdownOpen && quote && (
        <PriceBreakdownSheet
          quote={quote}
          productName={model.name}
          colorLabel={colorLabel}
          articleCount={1}
          onClose={() => setBreakdownOpen(false)}
        />
      )}
    </>
  );
}
